<template>
    <div class="*:my-4">
        <SearchViewSwitch view="by-part" />
        <LazyVisibleSearchPart v-for="part in resultsByPart" :key="part.Key" :part hydrate-on-visible>
            <template v-if="charts[part.Key]" #chart>
                <div class="hidden lg:block w-1/4">
                    <KeepAlive>
                        <PartChartPreview
                            v-if="part.Manufacturer"
                            :part="part"
                            :part-key="part.Key"
                            :initial-chart-data="charts[part.Key]"
                            preview-height="100"
                            @click="partChartDialog?.open(part, part.Key, $event)" />
                    </KeepAlive>
                </div>
            </template>
            <template #actions>
                <LazyInteractionPartAddToBom :part size="sm" hydrate-on-interaction="click" />
                <NotificationButton
                    v-if="part.PartId"
                    size="sm"
                    :disabled="isNotificationLoading"
                    :has-notification="hasNotifications[part.PartId] ?? false"
                    @click="openNotification(part)" />
            </template>
        </LazyVisibleSearchPart>
        <NotificationDialog ref="notificationDialog" @update="onNotificationUpdate" />
        <PartChartDialog ref="partChartDialog" />
    </div>
</template>
<script setup lang="ts">
const props = defineProps<{ searchResults: SearchResultsTransformed }>();
const isAuthenticated = useStateIsAuthenticated();
const { $api } = useNuxtApp();
const globalConfig = useStateGlobalConfig();

const charts = shallowRef<Record<string, PartChartData>>({});
const notifications = ref<Record<number, PartNotification>>({});
const hasNotifications = ref<Record<number, boolean>>({});
const isNotificationLoading = ref(false);
const notificationDialog = useTemplateRef("notificationDialog");
const partChartDialog = useTemplateRef("partChartDialog");
const searchText = globalConfig.value.SearchText;

const resultsByPart = computed(() => groupResultsByPart(searchText, props.searchResults));

function openNotification(part: PartDetailResultGroup) {
    const notification = part.PartId ? notifications.value[part.PartId] : null;
    notificationDialog.value?.openDialogPartResult(part, notification);
}

function onNotificationUpdate(notification: PartNotification) {
    notifications.value[notification.PartId] = notification;
    hasNotifications.value[notification.PartId] =
        notification.PriceAlerts.length > 0 || notification.StockAlerts.length > 0;
}

onMounted(() => {
    const fetchOptions = {
        body: {
            Parts: resultsByPart.value.map((item) => ({
                ManufacturerId: item.ManufacturerId,
                PartNumber: item.PartNumber,
            })),
        },
    };
    $api<PartChartData[]>("part-chart", { method: "POST", ...fetchOptions }).then((data) => {
        if (Array.isArray(data)) charts.value = Object.fromEntries(data.map((item) => [item.Key, item]));
    });

    if (isAuthenticated.value) {
        isNotificationLoading.value = true;
        $api<PartNotificationsResponse | null>("notifications", { method: "POST", ...fetchOptions })
            .then((data) => {
                if (data) {
                    notifications.value = Object.fromEntries(data.PartNotifications.map((n) => [n.PartId, n]));
                    hasNotifications.value = Object.fromEntries(data.PartNotifications.map((n) => [n.PartId, true]));
                } else {
                    notifications.value = {};
                    hasNotifications.value = {};
                }
            })
            .finally(() => {
                isNotificationLoading.value = false;
            });
    }
});
</script>
